import { useState, useEffect } from "react";
import { useNewsletterStore, NewsletterSubscriber } from "@/data/newsletterStore";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mail, Trash2, Download, FileSpreadsheet, FileText, Send, Users, Activity, CheckCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useSearchParams } from "react-router-dom";
import { getApiUrl } from "@/utils/apiConfig";

export default function AdminNewsletterPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { subscribers, isLoaded, fetchSubscribers, removeSubscriber } = useNewsletterStore();
  const { toast } = useToast();
  
  // Campaign state
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const handleDelete = (id: string) => {
    if (confirm("Supprimer cet abonné ?")) {
      removeSubscriber(id);
      toast({ title: "Abonné supprimé", variant: "destructive" });
    }
  };

  const exportCSV = () => {
    const csvContent = "data:text/csv;charset=utf-8," 
      + "Email,Date d'inscription\n"
      + subscribers.map(e => `${e.email},${new Date(e.date).toLocaleDateString('fr-FR')}`).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "inscrits_newsletter.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast({ title: "Export Excel (CSV) réussi" });
  };

  const exportWord = () => {
    const textContent = "data:text/plain;charset=utf-8,"
      + "Liste des inscrits à la Newsletter Kayn'in\n\n"
      + subscribers.map(e => `- ${e.email} (Inscrit le ${new Date(e.date).toLocaleDateString('fr-FR')})`).join("\n");
    
    const encodedUri = encodeURI(textContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "inscrits_newsletter.txt");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast({ title: "Export Word (TXT) réussi" });
  };

  const exportPDF = () => {
    // Basic print trick for PDF export
    window.print();
    toast({ title: "Fenêtre d'impression ouverte", description: "Choisissez 'Enregistrer au format PDF'." });
  };

  const handleDispatch = async () => {
    if (!subject || !content) {
      toast({ title: "Erreur", description: "Veuillez remplir l'objet et le contenu.", variant: "destructive" });
      return;
    }
    
    if (subscribers.length === 0) {
      toast({ title: "Erreur", description: "Aucun abonné dans la liste.", variant: "destructive" });
      return;
    }

    setIsSending(true);
    setProgress(20);
    
    try {
      const response = await fetch(`${getApiUrl()}/newsletter.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem('kaynin_auth_token') || ''}`
        },
        body: JSON.stringify({
          action: 'dispatch',
          subject,
          content
        })
      });

      setProgress(80);

      const data = await response.json();
      
      if (response.ok && data.success) {
        setProgress(100);
        setTimeout(() => {
          setIsSending(false);
          toast({ 
            title: "Campagne envoyée !", 
            description: `Votre newsletter a été distribuée avec succès à ${data.success_count} contacts.` 
          });
          setSubject("");
          setContent("");
        }, 500);
      } else {
        throw new Error(data.error || "Erreur lors de l'envoi");
      }
    } catch (err: any) {
      setIsSending(false);
      setProgress(0);
      toast({ 
        title: "Erreur d'envoi", 
        description: err.message, 
        variant: "destructive" 
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground flex items-center gap-2">
            <Mail className="w-8 h-8 text-primary" />
            Newsletter & Campagnes
          </h1>
          <p className="text-muted-foreground mt-1">Gérez vos abonnés et dispatchez vos campagnes e-mails (façon Mailchimp).</p>
        </div>
      </div>

      <Tabs value={searchParams.get("tab") || "inscrits"} onValueChange={(v) => setSearchParams({ tab: v })} className="w-full">
        <TabsList className="grid w-full max-w-[400px] grid-cols-2 mb-6">
          <TabsTrigger value="inscrits">Répertoire des Inscrits</TabsTrigger>
          <TabsTrigger value="campagnes">Nouvelle Campagne</TabsTrigger>
        </TabsList>

        <TabsContent value="inscrits" className="mt-0 space-y-6 print:block">
          <Card className="border shadow-sm">
            <CardHeader className="pb-3 border-b bg-muted/10 flex flex-col md:flex-row justify-between md:items-center gap-4 print:hidden">
              <div>
                <CardTitle className="text-lg">Liste des Abonnés ({subscribers.length})</CardTitle>
                <CardDescription>Citoyens ayant souscrit à la newsletter.</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={exportCSV} className="gap-2 text-green-700 border-green-200 hover:bg-green-50">
                  <FileSpreadsheet className="w-4 h-4" /> Excel
                </Button>
                <Button size="sm" variant="outline" onClick={exportWord} className="gap-2 text-blue-700 border-blue-200 hover:bg-blue-50">
                  <FileText className="w-4 h-4" /> Word
                </Button>
                <Button size="sm" variant="outline" onClick={exportPDF} className="gap-2 text-red-700 border-red-200 hover:bg-red-50">
                  <Download className="w-4 h-4" /> PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-muted/40 text-muted-foreground font-semibold uppercase text-xs">
                    <tr>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Date d'inscription</th>
                      <th className="px-4 py-3">Statut</th>
                      <th className="px-4 py-3 text-right print:hidden">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {!isLoaded ? (
                      <tr>
                        <td colSpan={4} className="text-center py-8 text-muted-foreground">Chargement des abonnés...</td>
                      </tr>
                    ) : subscribers.map((sub) => (
                      <tr key={sub.id} className="hover:bg-muted/10">
                        <td className="px-4 py-3 font-medium text-blue-600">{sub.email}</td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {new Date(sub.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute:'2-digit' })}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className="bg-emerald-100 text-emerald-800 border-none"><CheckCircle className="w-3 h-3 mr-1" /> Actif</Badge>
                        </td>
                        <td className="px-4 py-3 text-right print:hidden">
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(sub.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {isLoaded && subscribers.length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-center py-8 text-muted-foreground">Aucun abonné pour le moment.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="campagnes" className="mt-0 space-y-6 print:hidden">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card className="border shadow-sm">
                <CardHeader className="bg-muted/10 border-b">
                  <CardTitle className="text-lg flex items-center gap-2"><Send className="w-5 h-5 text-primary" /> Créer une Newsletter</CardTitle>
                  <CardDescription>Rédigez votre e-mail et envoyez-le à toute votre base de données.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 pt-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold">Objet de l'e-mail</label>
                    <Input 
                      placeholder="Ex: Nouveautés de la semaine sur Kayn'in !" 
                      value={subject}
                      onChange={e => setSubject(e.target.value)}
                      disabled={isSending}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold">Contenu du message (Texte / HTML)</label>
                    <Textarea 
                      placeholder="Rédigez votre newsletter ici..." 
                      className="min-h-[300px] resize-y"
                      value={content}
                      onChange={e => setContent(e.target.value)}
                      disabled={isSending}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="border shadow-sm bg-primary/5">
                <CardHeader>
                  <CardTitle className="text-lg">Détails du Dispatch</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center gap-4 bg-background p-4 rounded-xl border">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                      <Users className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground font-semibold">Audience cible</p>
                      <p className="font-bold text-lg">{subscribers.length} abonnés</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Button 
                      className="w-full h-12 text-md gap-2 font-bold bg-primary hover:bg-primary/90" 
                      onClick={handleDispatch}
                      disabled={isSending || subscribers.length === 0}
                    >
                      {isSending ? (
                        <>
                          <Activity className="w-5 h-5 animate-pulse" />
                          Génération en cours... {progress}%
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Générer et Dispatcher
                        </>
                      )}
                    </Button>
                    
                    {isSending && (
                      <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                        <div className="bg-primary h-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
                      </div>
                    )}
                    
                    <p className="text-xs text-center text-muted-foreground">
                      L'envoi sera effectué via notre serveur SMTP intégré.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
